import { useState, useMemo } from 'react';
import Swal from 'sweetalert2';
import type { SARadio, SARadioMasterStatus, StatusChangeEntry } from '@/mocks/superAdminRadios';

/* ═══════════════════════════════════════════════════
   STATUS CONFIG
═══════════════════════════════════════════════════ */

export const masterStatusConfig: Record<SARadioMasterStatus, {
  label: string;
  dotColor: string;
  textColor: string;
  bgColor: string;
  icon: string;
  description: string;
  order: number;
}> = {
  solicitud_recibida: {
    label: 'Solicitud Recibida',
    dotColor: 'bg-sky-400',
    textColor: 'text-sky-600',
    bgColor: 'bg-sky-50',
    icon: 'ri-mail-add-line',
    description: 'La radio envió su solicitud de ingreso a la plataforma.',
    order: 1,
  },
  en_revision: {
    label: 'En Revisión',
    dotColor: 'bg-amber-400',
    textColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
    icon: 'ri-search-eye-line',
    description: 'SA está evaluando la documentación y el stream.',
    order: 2,
  },
  aprobada: {
    label: 'Aprobada',
    dotColor: 'bg-emerald-400',
    textColor: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    icon: 'ri-check-double-line',
    description: 'Frecuencia reservada temporalmente. Radio aprobada.',
    order: 3,
  },
  pendiente_contrato: {
    label: 'Pendiente de Contrato',
    dotColor: 'bg-violet-400',
    textColor: 'text-violet-600',
    bgColor: 'bg-violet-50',
    icon: 'ri-file-text-line',
    description: 'Contrato generado. Esperando aceptación del dueño.',
    order: 4,
  },
  pendiente_firma: {
    label: 'Pendiente de Firma',
    dotColor: 'bg-indigo-400',
    textColor: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    icon: 'ri-pen-nib-line',
    description: 'Esperando firma electrónica del contrato.',
    order: 5,
  },
  pendiente_pago: {
    label: 'Pendiente de Pago',
    dotColor: 'bg-orange-400',
    textColor: 'text-orange-600',
    bgColor: 'bg-orange-50',
    icon: 'ri-bank-card-line',
    description: 'Esperando pago inicial para activar la radio.',
    order: 6,
  },
  activa: {
    label: 'Activa',
    dotColor: 'bg-emerald-500',
    textColor: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    icon: 'ri-play-circle-line',
    description: 'Radio operativa y transmitiendo normalmente.',
    order: 7,
  },
  suspendida: {
    label: 'Suspendida',
    dotColor: 'bg-red-500',
    textColor: 'text-red-500',
    bgColor: 'bg-red-50',
    icon: 'ri-pause-circle-line',
    description: 'Radio suspendida. Puede ser por falta de pago, inactividad o incumplimiento.',
    order: 8,
  },
  vencida: {
    label: 'Vencida',
    dotColor: 'bg-rose-500',
    textColor: 'text-rose-600',
    bgColor: 'bg-rose-50',
    icon: 'ri-timer-flash-line',
    description: 'Cuenta vencida. Licencia anual o plan expirado.',
    order: 9,
  },
  cancelada: {
    label: 'Cancelada',
    dotColor: 'bg-stone-500',
    textColor: 'text-stone-500',
    bgColor: 'bg-stone-100',
    icon: 'ri-close-circle-line',
    description: 'Cierre definitivo de la radio.',
    order: 10,
  },
  archivada: {
    label: 'Archivada',
    dotColor: 'bg-stone-400',
    textColor: 'text-stone-400',
    bgColor: 'bg-stone-50',
    icon: 'ri-archive-line',
    description: 'Histórico. Ya no aparece en listados activos.',
    order: 11,
  },
};

/* ── Allowed transitions ── */
const allowedTransitions: Record<SARadioMasterStatus, SARadioMasterStatus[]> = {
  solicitud_recibida: ['en_revision', 'cancelada'],
  en_revision: ['aprobada', 'cancelada', 'solicitud_recibida'],
  aprobada: ['pendiente_contrato', 'activa', 'cancelada'],
  pendiente_contrato: ['pendiente_firma', 'cancelada'],
  pendiente_firma: ['pendiente_pago', 'cancelada'],
  pendiente_pago: ['activa', 'cancelada'],
  activa: ['suspendida', 'vencida', 'cancelada'],
  suspendida: ['activa', 'vencida', 'cancelada'],
  vencida: ['activa', 'suspendida', 'cancelada'],
  cancelada: ['archivada'],
  archivada: [],
};

const statusOrder = Object.values(masterStatusConfig).sort((a, b) => a.order - b.order);

/* ═══════════════════════════════════════════════════
   COMPONENTS
═══════════════════════════════════════════════════ */

export function StatusBadge({ status, size }: { status: SARadioMasterStatus; size?: 'sm' | 'md' }) {
  const cfg = masterStatusConfig[status];
  const isSm = size === 'sm';
  return (
    <span className={`inline-flex items-center gap-1 ${isSm ? 'px-1.5 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]'} rounded-full font-bold whitespace-nowrap ${cfg.textColor} ${cfg.bgColor}`}>
      <span className={`${isSm ? 'w-1.5 h-1.5' : 'w-2 h-2'} rounded-full ${cfg.dotColor}`}></span>
      {cfg.label}
    </span>
  );
}

export function StatusTimeline({ history }: { history: StatusChangeEntry[] }) {
  return (
    <div className="space-y-0">
      {[...history].reverse().map((entry, idx) => {
        const cfg = masterStatusConfig[entry.toStatus];
        const prevCfg = entry.fromStatus ? masterStatusConfig[entry.fromStatus] : null;
        const isLast = idx === history.length - 1;

        return (
          <div key={entry.id} className="flex gap-3">
            {/* Timeline line + dot */}
            <div className="flex flex-col items-center">
              <div className={`w-2.5 h-2.5 rounded-full shrink-0 mt-1 ${cfg.dotColor} ring-2 ring-background-50`}></div>
              {!isLast && <div className="w-0.5 flex-1 bg-background-200 my-0.5"></div>}
            </div>

            {/* Content */}
            <div className={`pb-3 ${!isLast ? '' : ''}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[11px] font-bold ${cfg.textColor}`}>{cfg.label}</span>
                {prevCfg && (
                  <>
                    <i className="ri-arrow-right-line text-foreground-300 text-[10px]"></i>
                    <span className="text-foreground-400 text-[10px]">desde {prevCfg.label}</span>
                  </>
                )}
                {!prevCfg && (
                  <span className="text-foreground-300 text-[10px]">— inicio</span>
                )}
              </div>
              <p className="text-foreground-500 text-[10px] mt-0.5">{entry.reason}</p>
              <div className="flex items-center gap-2 mt-1 text-foreground-300 text-[9px]">
                <span className="font-medium">{entry.changedBy}</span>
                <span>·</span>
                <span>{new Date(entry.changedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })} — {new Date(entry.changedAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface StatusChangeModalProps {
  radio: SARadio;
  onClose: () => void;
  onStatusChange: (radio: SARadio, newStatus: SARadioMasterStatus, reason: string) => void;
}

export default function StatusChangeModal({ radio, onClose, onStatusChange }: StatusChangeModalProps) {
  const currentCfg = masterStatusConfig[radio.status];
  const [selectedStatus, setSelectedStatus] = useState<SARadioMasterStatus | ''>('');
  const [reason, setReason] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const availableTransitions = useMemo(() => {
    const allowed = allowedTransitions[radio.status] || [];
    return allowed.map((statusKey) => ({
      key: statusKey,
      cfg: masterStatusConfig[statusKey],
    }));
  }, [radio.status]);

  const handleChange = async () => {
    if (!selectedStatus) return;
    if (!reason.trim()) {
      await Swal.fire({
        icon: 'warning',
        title: 'Motivo requerido',
        text: 'Escribí el motivo del cambio de estado.',
        confirmButtonColor: '#f59e0b',
        customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap' },
        buttonsStyling: false,
      });
      return;
    }

    const newCfg = masterStatusConfig[selectedStatus];

    const result = await Swal.fire({
      title: '¿Confirmar cambio?',
      html: `
        <div style="text-align:left;font-size:13px">
          <p style="margin-bottom:8px">Vas a cambiar <strong>${radio.name}</strong> de:</p>
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;justify-content:center">
            <span style="display:inline-flex;align-items:center;gap:4px;padding:4px 12px;border-radius:9999px;font-size:11px;font-weight:700;${currentCfg.textColor.replace('text-', 'color:')};background:${currentCfg.bgColor.replace('bg-', '') === 'emerald-50' ? '#ecfdf5' : currentCfg.bgColor.replace('bg-', '') === 'amber-50' ? '#fefce8' : currentCfg.bgColor.replace('bg-', '') === 'red-50' ? '#fef2f2' : currentCfg.bgColor.replace('bg-', '') === 'sky-50' ? '#f0f9ff' : currentCfg.bgColor.replace('bg-', '') === 'violet-50' ? '#f5f3ff' : currentCfg.bgColor.replace('bg-', '') === 'indigo-50' ? '#eef2ff' : currentCfg.bgColor.replace('bg-', '') === 'orange-50' ? '#fff7ed' : currentCfg.bgColor.replace('bg-', '') === 'rose-50' ? '#fff1f2' : currentCfg.bgColor.replace('bg-', '') === 'stone-100' ? '#f5f5f4' : currentCfg.bgColor.replace('bg-', '') === 'stone-50' ? '#fafaf9' : '#f9fafb'}">
              ${currentCfg.label}
            </span>
            <span style="font-size:14px;color:#9ca3af">→</span>
            <span style="display:inline-flex;align-items:center;gap:4px;padding:4px 12px;border-radius:9999px;font-size:11px;font-weight:700;${newCfg.textColor.replace('text-', 'color:')};background:${newCfg.bgColor.replace('bg-', '') === 'emerald-50' ? '#ecfdf5' : newCfg.bgColor.replace('bg-', '') === 'amber-50' ? '#fefce8' : newCfg.bgColor.replace('bg-', '') === 'red-50' ? '#fef2f2' : newCfg.bgColor.replace('bg-', '') === 'sky-50' ? '#f0f9ff' : newCfg.bgColor.replace('bg-', '') === 'violet-50' ? '#f5f3ff' : newCfg.bgColor.replace('bg-', '') === 'indigo-50' ? '#eef2ff' : newCfg.bgColor.replace('bg-', '') === 'orange-50' ? '#fff7ed' : newCfg.bgColor.replace('bg-', '') === 'rose-50' ? '#fff1f2' : newCfg.bgColor.replace('bg-', '') === 'stone-100' ? '#f5f5f4' : newCfg.bgColor.replace('bg-', '') === 'stone-50' ? '#fafaf9' : '#f9fafb'}">
              ${newCfg.label}
            </span>
          </div>
          <div style="background:#f3f4f6;border-radius:10px;padding:10px 12px;font-size:11px;color:#4b5563;">
            <strong style="color:#1f2937">Motivo:</strong> ${reason}
          </div>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, cambiar estado',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#d1d5db',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold text-sm',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    });

    if (result.isConfirmed) {
      onStatusChange(radio, selectedStatus, reason.trim());
      onClose();
    }
  };

  const reasonPresets: Record<SARadioMasterStatus, string[]> = {
    suspendida: [
      'Falta de pago de licencia',
      'Licencia anual vencida',
      'Inactividad prolongada del stream',
      'Incumplimiento de términos y condiciones',
      'Contenido inapropiado reportado',
    ],
    vencida: [
      'Licencia anual de frecuencia RFW vencida',
      'Plan vencido sin renovación',
      'Período de gracia finalizado',
    ],
    cancelada: [
      'Cierre solicitado por el dueño',
      'Violación grave de términos',
      'Cuenta inactiva por más de 90 días',
      'Frecuencia reasignada por incumplimiento',
    ],
    activa: [
      'Pago recibido y verificado',
      'Reactivación tras suspensión',
      'Renovación de licencia confirmada',
      'Nuevo contrato firmado y pago confirmado',
    ],
    archivada: [
      'Archivado automático tras 7 días en cancelada',
      'Limpieza de registros históricos',
    ],
    en_revision: ['Inicio de revisión de documentación'],
    aprobada: [
      'Documentación verificada completamente',
      'Stream verificado y funcionando correctamente',
    ],
    pendiente_contrato: ['Contrato generado automáticamente'],
    pendiente_firma: ['Contrato enviado para firma electrónica'],
    pendiente_pago: ['Firma completada. Pendiente de pago inicial.'],
    solicitud_recibida: ['Reapertura de caso para revisión adicional'],
  };

  const presets = reasonPresets[selectedStatus as SARadioMasterStatus] || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={onClose}></div>

      {/* Modal */}
      <div
        className="relative bg-background-50 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-xl flex flex-col"
        style={{ animation: 'fadeInUp 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-background-200 shrink-0">
          <div>
            <h3 className="text-foreground-950 font-bold text-sm">Cambiar Estado</h3>
            <p className="text-foreground-400 text-[10px] mt-0.5">{radio.name} · {radio.frequency}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5 overflow-y-auto flex-1">
          {/* Current status */}
          <div className="bg-background-100 rounded-xl p-4">
            <p className="text-foreground-400 text-[10px] font-medium mb-2">Estado actual</p>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${currentCfg.bgColor} flex items-center justify-center`}>
                <i className={`${currentCfg.icon} ${currentCfg.textColor} text-base`}></i>
              </div>
              <div>
                <p className={`text-sm font-bold ${currentCfg.textColor}`}>{currentCfg.label}</p>
                <p className="text-foreground-400 text-[10px]">{currentCfg.description}</p>
              </div>
            </div>

            {/* Order stepper */}
            <div className="mt-4 flex items-center gap-0.5 flex-wrap">
              {statusOrder.map((s, i) => {
                const isCurrent = masterStatusConfig[radio.status].order === s.order;
                const isPast = s.order < masterStatusConfig[radio.status].order;
                return (
                  <div key={s.order} className="flex items-center">
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold transition-colors ${
                        isCurrent ? 'bg-foreground-950 text-background-50' : isPast ? 'bg-emerald-100 text-emerald-600' : 'bg-background-200 text-foreground-300'
                      }`}
                      title={s.label}
                    >
                      {isPast ? <i className="ri-check-line text-[8px]"></i> : s.order}
                    </div>
                    {i < statusOrder.length - 1 && (
                      <div className={`w-2.5 h-0.5 ${isPast ? 'bg-emerald-200' : 'bg-background-200'}`}></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* New status selector */}
          <div>
            <label className="text-foreground-500 text-[10px] font-bold uppercase tracking-wider block mb-2">
              Nuevo estado
            </label>
            {availableTransitions.length === 0 ? (
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 text-center">
                <i className="ri-forbid-line text-stone-400 text-xl block mb-1"></i>
                <p className="text-stone-500 text-xs font-medium">No hay transiciones disponibles</p>
                <p className="text-stone-400 text-[10px] mt-0.5">Este estado es terminal o no permite cambios.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {availableTransitions.map(({ key: statusKey, cfg }) => (
                  <button
                    key={statusKey}
                    onClick={() => setSelectedStatus(selectedStatus === statusKey ? '' : statusKey)}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border-2 text-left cursor-pointer transition-all ${
                      selectedStatus === statusKey
                        ? 'border-foreground-950 bg-foreground-950/5'
                        : 'border-background-200 hover:border-foreground-300 bg-background-50'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg ${cfg.bgColor} flex items-center justify-center shrink-0`}>
                      <i className={`${cfg.icon} ${cfg.textColor} text-sm`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className={`text-xs font-bold ${cfg.textColor} truncate`}>{cfg.label}</p>
                      <p className="text-foreground-300 text-[9px] truncate">{cfg.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Reason */}
          {selectedStatus && (
            <div>
              <label className="text-foreground-500 text-[10px] font-bold uppercase tracking-wider block mb-2">
                Motivo del cambio
              </label>

              {presets.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {presets.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setReason(preset)}
                      className={`px-2.5 py-1 rounded-lg text-[10px] font-medium cursor-pointer transition-colors whitespace-nowrap ${
                        reason === preset
                          ? 'bg-foreground-950 text-background-50'
                          : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                      }`}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              )}

              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Describí el motivo del cambio de estado..."
                maxLength={500}
                rows={3}
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 resize-none transition-colors"
              ></textarea>
              <p className="text-foreground-300 text-[9px] text-right mt-1">{reason.length}/500</p>
            </div>
          )}

          {/* History toggle */}
          <div>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-1.5 text-foreground-400 text-[10px] font-medium hover:text-foreground-600 cursor-pointer transition-colors"
            >
              <i className={`${showHistory ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`}></i>
              Historial completo ({radio.statusHistory.length} cambios)
            </button>

            {showHistory && (
              <div className="mt-3 bg-background-100 rounded-xl p-4 max-h-48 overflow-y-auto">
                <StatusTimeline history={radio.statusHistory} />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 p-5 border-t border-background-200 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleChange}
            disabled={!selectedStatus || !reason.trim()}
            className={`px-5 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
              selectedStatus && reason.trim()
                ? 'bg-amber-500 text-background-50 hover:bg-amber-600'
                : 'bg-background-200 text-foreground-300 cursor-not-allowed'
            }`}
          >
            <i className="ri-arrow-left-right-line text-xs"></i>
            Cambiar estado
          </button>
        </div>
      </div>
    </div>
  );
}